import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getGradeLevels,
  createGradeLevel,
  updateGradeLevel,
  deleteGradeLevel,
  getGradeLevelById,
} from '../services/gradeLevelsService';
import Notification from '../components/Common/Notification';

export const GradeLevelContext = createContext();

export const GradeLevelProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [gradeLevels, setGradeLevels] = useState([]);
  const [selectedGradeLevel, setSelectedGradeLevel] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllGradeLevels = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getGradeLevels(authHeaders);
      setGradeLevels(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching grade levels.';
      showNotification(errorMessage, true);
    }
  };

  const fetchGradeLevelById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getGradeLevelById(id, authHeaders);
      setSelectedGradeLevel(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching grade level with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addGradeLevel = async (gradeLevelData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newGradeLevel = await createGradeLevel(gradeLevelData, authHeaders);
      setGradeLevels(prevGradeLevels => [...prevGradeLevels, newGradeLevel]);
      showNotification('Grade level added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding grade level.';
      showNotification(errorMessage, true);
    }
  };

  const editGradeLevel = async (id, gradeLevelData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedGradeLevel = await updateGradeLevel(id, gradeLevelData, authHeaders);
      setGradeLevels(prevGradeLevels =>
        prevGradeLevels.map(level => (level.id === id ? updatedGradeLevel : level))
      );
      showNotification('Grade level updated successfully!', false);
      return updatedGradeLevel;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating grade level.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeGradeLevel = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteGradeLevel(id, authHeaders);
      setGradeLevels(prevGradeLevels => prevGradeLevels.filter(level => level.id !== id));
      showNotification('Grade level removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing grade level.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllGradeLevels();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <GradeLevelContext.Provider
      value={{
        gradeLevels,
        selectedGradeLevel,
        fetchAllGradeLevels,
        fetchGradeLevelById,
        addGradeLevel,
        editGradeLevel,
        removeGradeLevel,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </GradeLevelContext.Provider>
  );
};

export const useGradeLevel = () => useContext(GradeLevelContext);
